import React from 'react'
import { createRoot, hydrateRoot, Root } from 'react-dom/client'
import { LAZY_BUNDLES } from './lazy-bundles'
import { onRegister, registry } from './registry'

// React 18 requires createRoot to be called exactly once per DOM node (unlike
// legacy ReactDOM.render, which could be called repeatedly). Tracking roots
// here lets unmount-components.ts call .unmount() on the right one instead of
// the removed ReactDOM.unmountComponentAtNode.
export const roots = new Map<Element, Root>()

const log = (
  level: 'debug' | 'info' | 'warn' | 'error',
  ...args: unknown[]
) => {
  if (level === 'error') return console.error(...args)
  if (process.env.NODE_ENV === 'production') return
  console[level](...args)
}

type ReactHydrateOptions = {
  name?: string
  root?: string
}

// Hydration runs several times per page load, so in-flight names are tracked.
const loading = new Set<string>()

// Fire-and-forget: the chunk registers itself, and onRegister below hydrates it.
const loadBundleFor = (name: string) => {
  const load = LAZY_BUNDLES[name]
  if (!load || loading.has(name)) return

  loading.add(name)
  // eslint-disable-next-line promise/prefer-await-to-then
  load().catch((e) => {
    loading.delete(name)
    log('error', `Failed to load the bundle providing ${name}`, e)
  })
}

export const hydrateReactComponents = ({
  name,
  root,
}: ReactHydrateOptions = {}) => {
  let query = '.vite-rails-react-component'
  if (name) query += `[data-component-name="${name}"]`
  if (root) query = root + ' ' + query
  const componentTag = document.querySelectorAll(query)
  componentTag.forEach((tag) => {
    try {
      hydrateComponent(tag)
    } catch (e) {
      log('error', `Error hydrating component ${tag.id}`, e)
    }
  })
}

const hydrateComponent = (scriptTag: Element) => {
  const name = scriptTag.getAttribute('data-component-name')
  const id = scriptTag.getAttribute('data-component-id')
  if (!id || !name) return

  const props = JSON.parse(scriptTag.innerHTML) as Record<string, unknown>
  const component = registry.get(name)
  if (!component) {
    if (LAZY_BUNDLES[name]) loadBundleFor(name)
    else log('warn', `Component ${name} not registered`)
    return
  }

  const node = document.getElementById(id)
  if (!node) return

  const element: React.ReactElement = React.createElement(component, props)

  // hydrateReactComponents() can fire more than once against the same
  // still-mounted node in the same burst (e.g. the top-level call, turbo:load,
  // and a component's onRegister callback all firing for one page load, or a
  // data-turbo-permanent nav on turbo:load and turbo:frame-render, none of
  // which unmount first). Legacy ReactDOM.render/hydrate were safely
  // reentrant on a container; createRoot/hydrateRoot are not — each call
  // makes an independent root — so a node that already has one is skipped
  // rather than hydrated/rendered again. Re-rendering the existing root here
  // is unsafe too: calling .render() while its initial hydrateRoot() is
  // still completing can abort that hydration mid-flight and force a fresh
  // client render, which drops the original instance's effects (and, seen in
  // practice, can leave stray exit-animation DOM nodes behind in
  // framer-motion's AnimatePresence). The props are identical across these
  // burst calls (nothing on the page has changed yet), so there's nothing to
  // apply anyway — a genuinely new server render (e.g. after real navigation)
  // always lands on a fresh DOM node with no Map entry, and unmount-
  // components.ts removes the entry when this instance is actually torn down.
  if (roots.has(node)) return

  if (node.hasChildNodes()) {
    roots.set(node, hydrateRoot(node, element))
  } else {
    const root = createRoot(node)
    root.render(element)
    roots.set(node, root)
  }
}

onRegister((name) => hydrateReactComponents({ name }))
